import { useContext } from 'react'

import { Button, ModalContext, SidebarModal } from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useAgencyOrderBundle from '@hooks/useAgencyOrderBundle'
import SelectAgencies from './components/SelectAgencies'
import useBulkCreateAgencyOrderBundles from './hooks/useBulkCreateAgencyOrderBundles'

const MODAL_KEY = 'BulkCreateAgencyOrderBundlesModal'

type ModalCallbacks = {
  closeModal: () => void,
  bulkCreateAgencyOrderBundles: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const BulkCreateAgencyOrderBundlesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, bulkCreateAgencyOrderBundles } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { agencyOrderBundle } = modalPayload
  // agency id to be used for order_bundle_ids via the agency bundles page
  const { order_bundle_id, agency_id } = agencyOrderBundle

  const bulkCreateAgencyOrderBundlesPayload = useBulkCreateAgencyOrderBundles(agencyOrderBundle)
  const {
    entityParams,
    saveEnabled,
    updating,
  } = bulkCreateAgencyOrderBundlesPayload

  const {
    callbacks: {
      bulkCreateAgencyOrderBundles: bulkFn,
    },
  } = useAgencyOrderBundle(agencyOrderBundle)

  const agencyOrderBundlesPayload = {
    callbacks: {
      action: bulkFn,
      afterAction: closeModal,
    },
    entityParams,
    requestOptions: { include_brand_brand_groups: true },
    toastText: 'Agencies assigned',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title="Assign"
        titleSecondLine="Agencies"
      />

      <SidebarModal.Content>
        {!!order_bundle_id && (
          <SelectAgencies bulkCreateAgencyOrderBundlesPayload={bulkCreateAgencyOrderBundlesPayload} />
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => bulkCreateAgencyOrderBundles(agencyOrderBundlesPayload)}
          size="large"
        >
          {saveEnabled ? 'Assign Agencies' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkCreateAgencyOrderBundlesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
